/**
 * This file is a repository for data that would be nice to get directly from jsonschema
 */
import { Indexable, UiSchemaKey } from './utils/util';
import { IconType } from './components';

export enum EventType {
    Deleted = 'deleted',
    Created = 'created',
    Updated = 'updated',
}
export interface Event {
    after: string;
    before: string;
    category: string;
    created: string;
    eventType: EventType;
    id: string;
    itemDisplayName: string;
    itemId: string;
    updatedBy: string;
}

export interface EventRollupChange {
    category: string;
    firstValue: any;
    lastUpdated: string;
    lastUpdatedBy: string;
    lastValue: any;
    property: string;
}
export interface EventRollup {
    category: string;
    changes: {
        [propName: string]: EventRollupChange;
    };
    firstUpdate: string;
    itemDisplayName: string;
    itemId: string;
    lastUpdate: string;
    orderedChanges: EventRollupChange[];
    rollupEventType: EventType;
}

export interface Category {
    label: string;
    description: string;
    key: string;
    asset: keyof typeof Assets;
    editAsset: keyof typeof EditAssets;
    iconName: string;
    iconType: IconType;
    hidden?: boolean;
}

export interface Asset {
    label: string;
    description: string;
    category: keyof typeof Categories;
}

export const Categories: Indexable<Category> = {
    papers: {
        label: 'Papers',
        description: 'AI2 Papers…',
        key: 'id',
        asset: 'paper',
        editAsset: 'paperOpts',
        iconName: 'paper',
        iconType: 'url',
    },
    datasets: {
        label: 'Datasets',
        description: 'AI2 Datasets…',
        key: 'id',
        asset: 'dataset',
        editAsset: 'datasetOpts',
        iconName: 'data',
        iconType: 'url',
    },
};

export const Assets: Indexable<Asset> = {
    paper: {
        label: 'Paper',
        description: 'An AI2 Paper…',
        category: 'papers',
    },
    dataset: {
        label: 'Dataset',
        description: 'An AI2 Dataset…',
        category: 'datasets',
    },
};

export const EditAssets: Indexable<Asset> = {
    paperOpts: Assets.paper,
    datasetOpts: Assets.dataset,
};

type SupportedWidgetProps =
    | 'displayFormat'
    | 'format'
    | 'label'
    | 'labelSrc'
    | 'maxLength'
    | 'rows'
    | 'urlSrc'
    | 'urlSrcPrefix';

export interface Properties {
    title?: string;
    type?: string;
    example?: string;
    description?: string;
    [UiSchemaKey.Widget]?: UiWidgets;
    [UiSchemaKey.WidgetProps]?: Record<SupportedWidgetProps, any>;
    [UiSchemaKey.WidgetTable]?: UiWidgets;
    [UiSchemaKey.WidgetTableProps]?: Record<SupportedWidgetProps, any>;
    [UiSchemaKey.WidgetDetail]?: UiWidgets;
    [UiSchemaKey.WidgetDetailProps]?: Record<SupportedWidgetProps, any>;
    items?: Properties[];
}

export interface PropertyBag {
    properties: Indexable<Properties>;
}

export const hasProperties = (pred: any): pred is PropertyBag => {
    const typedPred = pred as PropertyBag;
    return typedPred.properties !== undefined;
};

export enum UiWidgets {
    Bold = 'bold',
    Date = 'date',
    DateTime = 'datetime',
    Link = 'link',
    LinkList = 'linkList',
    Markdown = 'markdown',
    Omit = 'omit',
    ShowMore = 'show-more',
    Tags = 'tags',
    TitleLink = 'title-link',
    Truncated = 'truncated',
    YesNo = 'yes-no',
}
